import { api } from "@/core/api";
import type {
  ClubSubscriptionConfig,
  RestPricingBreakdown,
  RestClubFeatureType,
  ClubSubscriptionResponse,
  SetupIntentResponse,
  GenericResponse,
  RestartSubscriptionResponse,
  InvoiceListResponse,
  FeatureUpdateResponse,
  SubscriptionMetricsResponse,
  WebhookEventsResponse,
  TrialInfoResponse,
  StartTrialResponse,
  RestBillingFrequency,
  BillingOptions,
  ChangeBillingFrequencyResponse,
} from "@/types/subscription";

const API_BASE = "/api/admin";

export interface SubscriptionStatusResponse {
  clubId: number;
  clubShortName: string;
  clubLongName: string;
  subscriptionConfig: ClubSubscriptionConfig;
  pricingBreakdown: RestPricingBreakdown;
  currentDate: string;
  isCorporate: boolean;
}

interface SubscriptionUpdateResponse {
  success: boolean;
  message: string;
  pricingBreakdown: RestPricingBreakdown;
}

interface PioneerStatusResponse {
  success: boolean;
  isPioneerClub: boolean;
  message: string;
}

interface CorporateStatusResponse {
  success: boolean;
  isCorporate: boolean;
  message: string;
}

interface SetClubTrialResponse {
  success: boolean;
  message: string;
  trialEndDate: string; // ISO yyyy-mm-dd
}

interface AllSubscriptionsResponse {
  subscriptions: ClubSubscriptionSummary[];
  featureEnabled: boolean;
}

interface FeatureStatusResponse {
  subscriptionsEnabled: boolean;
}

export interface ClubSubscriptionSummary {
  clubId: number;
  clubShortName: string;
  clubLongName: string;
  district: string;
  division: string;
  area: string;
  contactEmail: string;
  memberCount: number;
  subscriptionStatus: string;
  isPioneerClub: boolean;
  monthlyCharge: number;
  billingFrequency: string; // "monthly" | "yearly"
  chargeAmount: number; // actual amount charged per billing cycle, in pence
  trialEndDate?: string;
  gracePeriodEndDate?: string;
  enabledFeatures: string[];
}

/**
 * Subscription/billing REST client.
 *
 * Reference implementation for the shared API layer (issue #1963, Phase 1):
 * every method goes through `api` (the typed wrapper over axios, which keeps
 * the `X-Request-Id` / `X-Session-Id` interceptors) instead of importing
 * `axios` directly, returns a typed DTO, and throws a normalized `ApiError`
 * on failure (callers route it through `handleApiError`). The per-call
 * `fallbackMessage` is the user-facing text shown when the server response
 * carries no message of its own.
 */
class SubscriptionApiService {
  /**
   * Check if subscription features are enabled (public endpoint)
   */
  async getPublicFeatureStatus(): Promise<FeatureStatusResponse> {
    return api.get<FeatureStatusResponse>(`/api/subscription/public-status`, {
      fallbackMessage: "Failed to load subscription feature status",
    });
  }

  /**
   * Get all club subscriptions (system admin only)
   */
  async getAllSubscriptions(): Promise<AllSubscriptionsResponse> {
    return api.get<AllSubscriptionsResponse>(`${API_BASE}/clubs`, {
      fallbackMessage: "Failed to load club subscriptions",
    });
  }

  /**
   * Get subscription status for a specific club
   */
  async getClubSubscriptionStatus(clubId: number): Promise<SubscriptionStatusResponse> {
    return api.get<SubscriptionStatusResponse>(`${API_BASE}/clubs/${clubId}/subscription`, {
      fallbackMessage: "Failed to load subscription status",
    });
  }

  /**
   * Update subscription features for a club
   */
  async updateClubFeatures(
    clubId: number,
    enabledFeatures: RestClubFeatureType[],
  ): Promise<SubscriptionUpdateResponse> {
    return api.put<SubscriptionUpdateResponse>(
      `${API_BASE}/clubs/${clubId}/subscription/features`,
      { enabledFeatures },
      { fallbackMessage: "Failed to update subscription features" },
    );
  }

  /**
   * Toggle pioneer status for a club (system admin only)
   */
  async togglePioneerStatus(clubId: number): Promise<PioneerStatusResponse> {
    return api.post<PioneerStatusResponse>(`${API_BASE}/clubs/${clubId}/subscription/pioneer-toggle`, undefined, {
      fallbackMessage: "Failed to toggle pioneer status",
    });
  }

  /**
   * Set corporate club status (system admin only). Idempotent: the backend
   * only updates and logs when the requested value differs from the stored one.
   */
  async setCorporateStatus(clubId: number, isCorporate: boolean): Promise<CorporateStatusResponse> {
    return api.post<CorporateStatusResponse>(
      `${API_BASE}/clubs/${clubId}/subscription/corporate-status`,
      { isCorporate },
      { fallbackMessage: "Failed to set corporate status" },
    );
  }

  /**
   * Set or extend a club's trial period to a custom end date (system admin only).
   * @param trialEndDate ISO yyyy-mm-dd date the trial should end on (must be in the future)
   */
  async setClubTrial(clubId: number, trialEndDate: string): Promise<SetClubTrialResponse> {
    return api.post<SetClubTrialResponse>(
      `${API_BASE}/clubs/${clubId}/subscription/set-trial`,
      { trialEndDate },
      { fallbackMessage: "Failed to set trial period" },
    );
  }

  /**
   * Get subscription metrics dashboard data (system admin only)
   */
  async getSubscriptionMetrics(): Promise<SubscriptionMetricsResponse> {
    return api.get<SubscriptionMetricsResponse>(`${API_BASE}/subscriptions/metrics`, {
      fallbackMessage: "Failed to load subscription metrics",
    });
  }

  /**
   * Get webhook events with optional filtering (system admin only)
   */
  async getWebhookEvents(options?: {
    from?: number;
    count?: number;
    eventType?: string;
    status?: string;
    clubId?: number;
  }): Promise<WebhookEventsResponse> {
    const params = new URLSearchParams();
    if (options?.from !== undefined) params.append("from", options.from.toString());
    if (options?.count !== undefined) params.append("count", options.count.toString());
    if (options?.eventType) params.append("eventType", options.eventType);
    if (options?.status) params.append("status", options.status);
    if (options?.clubId !== undefined) params.append("clubId", options.clubId.toString());

    const queryString = params.toString();
    const url = `${API_BASE}/webhook-events${queryString ? `?${queryString}` : ""}`;

    return api.get<WebhookEventsResponse>(url, {
      fallbackMessage: "Failed to load webhook events",
    });
  }

  // ===========================
  // Club-Facing Endpoints (Club Officers)
  // ===========================

  /**
   * Get subscription details for a club (club officers only)
   */
  async getClubSubscription(clubId: number): Promise<ClubSubscriptionResponse> {
    return api.get<ClubSubscriptionResponse>(`/api/club/${clubId}/subscription`, {
      fallbackMessage: "Failed to load subscription details",
    });
  }

  /**
   * Create a Stripe SetupIntent for adding a payment method (club officers only)
   */
  async createSetupIntent(clubId: number): Promise<SetupIntentResponse> {
    return api.post<SetupIntentResponse>(`/api/club/${clubId}/subscription/setup-intent`, undefined, {
      fallbackMessage: "Failed to start payment setup",
    });
  }

  /**
   * Confirm and attach a payment method to the subscription (club officers only)
   */
  async confirmPaymentMethod(clubId: number, paymentMethodId: string): Promise<GenericResponse> {
    return api.post<GenericResponse>(
      `/api/club/${clubId}/subscription/payment-method`,
      { paymentMethodId },
      { fallbackMessage: "Failed to save payment method" },
    );
  }

  /**
   * Remove the payment method from the subscription (club officers only)
   */
  async removePaymentMethod(clubId: number): Promise<GenericResponse> {
    return api.delete<GenericResponse>(`/api/club/${clubId}/subscription/payment-method`, {
      fallbackMessage: "Failed to remove payment method",
    });
  }

  /**
   * Get invoice history for a club (club officers only)
   */
  async getInvoices(clubId: number): Promise<InvoiceListResponse> {
    return api.get<InvoiceListResponse>(`/api/club/${clubId}/subscription/invoices`, {
      fallbackMessage: "Failed to load invoices",
    });
  }

  /**
   * Update subscription features for a club (club officers only)
   */
  async updateClubFeaturesAsOfficer(
    clubId: number,
    enabledFeatures: RestClubFeatureType[],
  ): Promise<FeatureUpdateResponse> {
    return api.put<FeatureUpdateResponse>(
      `/api/club/${clubId}/subscription/features`,
      { enabledFeatures },
      { fallbackMessage: "Failed to update subscription features" },
    );
  }

  /**
   * Cancel subscription for a club (club officers only)
   */
  async cancelSubscription(clubId: number, reason?: string): Promise<GenericResponse> {
    return api.post<GenericResponse>(
      `/api/club/${clubId}/subscription/cancel`,
      { reason },
      { fallbackMessage: "Failed to cancel subscription" },
    );
  }

  /**
   * Reactivate a subscription whose cancellation is scheduled (club officers only)
   */
  async reactivateSubscription(clubId: number): Promise<GenericResponse> {
    return api.post<GenericResponse>(`/api/club/${clubId}/subscription/reactivate`, undefined, {
      fallbackMessage: "Failed to reactivate subscription",
    });
  }

  /**
   * Restart a cancelled subscription (club officers only)
   */
  async restartSubscription(clubId: number): Promise<RestartSubscriptionResponse> {
    return api.post<RestartSubscriptionResponse>(`/api/club/${clubId}/subscription/restart`, undefined, {
      fallbackMessage: "Failed to restart subscription",
    });
  }

  /**
   * Get trial/subscription info for banner display (any authenticated user)
   */
  async getTrialInfo(clubId: number): Promise<TrialInfoResponse> {
    return api.get<TrialInfoResponse>(`/api/club/${clubId}/subscription/trial-info`, {
      timeout: 10000, // 10 second timeout to prevent indefinite hangs
      fallbackMessage: "Failed to load trial information",
    });
  }

  /**
   * Start a trial for a club (officers only)
   */
  async startTrial(clubId: number): Promise<StartTrialResponse> {
    return api.post<StartTrialResponse>(`/api/club/${clubId}/subscription/start-trial`, undefined, {
      fallbackMessage: "Failed to start trial",
    });
  }

  // ===========================
  // Billing Frequency Endpoints
  // ===========================

  /**
   * Get billing options for a club (club officers only)
   * Returns current frequency, monthly price, yearly price, and savings info
   */
  async getBillingOptions(clubId: number): Promise<BillingOptions> {
    return api.get<BillingOptions>(`/api/club/${clubId}/subscription/billing-options`, {
      fallbackMessage: "Failed to load billing options",
    });
  }

  /**
   * Change billing frequency for a club (club officers only)
   * Monthly → Yearly: Immediate proration (charges yearly minus credit for unused month)
   * Yearly → Monthly: Takes effect at period end (no refund)
   */
  async changeBillingFrequency(
    clubId: number,
    billingFrequency: RestBillingFrequency,
  ): Promise<ChangeBillingFrequencyResponse> {
    return api.put<ChangeBillingFrequencyResponse>(
      `/api/club/${clubId}/subscription/billing-frequency`,
      { billingFrequency },
      { fallbackMessage: "Failed to change billing frequency" },
    );
  }
}

export const subscriptionApi = new SubscriptionApiService();
